import React from 'react';
import { RichText, Status, Tooltip } from '_atoms';
import type { TableColumnType } from '_atoms/Table/types';
import { getJobStatusColor } from '_templates/Candidates/utils';
import { headerCellRender } from '_templates/Jobs/constants';
import { STATUSES } from 'constants/user';
import type { AnalyticsCandidatesApiResponse } from '_templates/utils';

export interface TableSorting {
  field: string;
  key: string;
  order: string;
}

export interface AnalyticsCandidatesWidgetResponse extends AnalyticsCandidatesApiResponse {
  count: number;
  next: string | null;
  previous: string | null;
}

type SetSorting = (sort: TableSorting) => void;

export interface AnalyticsCandidateTableOptions {
  sorting: TableSorting;
  limit: { value: number; label: number };
  page: number;
  offset: number;
  itemsCount: number;
  next?: boolean;
  prev?: boolean;
}

export interface AnalyticsCandidateOfferCell {
  data: string;
  className: string | null;
}

export interface AnalyticsCandidateRow {
  job: string;
  candidate_full_name: string;
  country: string | null;
  responsible_recruiter?: string;
  step: string;
  step_status: string;
  candidate_status: string;
  assignment_date: string;
  interviews: string;
  comment: string;
  has_offer: AnalyticsCandidateOfferCell;
  offer_decision: AnalyticsCandidateOfferCell;
  result: string;
  [key: string]: unknown;
}

export const COLUMNS = (sort: TableSorting, setSorting: SetSorting): TableColumnType<AnalyticsCandidateRow>[] => [
  {
    title: headerCellRender('Job name', 'Job', 'job', sort, setSorting),
    dataIndex: 'job',
    key: 'job',
    width: 246,
    fixed: 'left',
    sorter: true,
    render: data => <Tooltip label={data as string}>{data as string}</Tooltip>,
  },
  {
    title: headerCellRender('Candidate', 'Candidate name', 'candidate_full_name', sort, setSorting),
    dataIndex: 'candidate_full_name',
    key: 'candidate_full_name',
    width: 168,
    fixed: 'left',
    sorter: true,
    render: data => <Tooltip label={data as string}>{data as string}</Tooltip>,
  },
  {
    title: headerCellRender('Country', 'Country', 'country', sort, setSorting),
    dataIndex: 'country',
    key: 'country',
    width: 168,
    sorter: true,
    render: data => (data ? <Tooltip label={data as string}>{data as string}</Tooltip> : '—'),
  },
  {
    title: 'Recruiter',
    dataIndex: 'responsible_recruiter',
    key: 'responsible_recruiter',
    width: 168,
    sorter: true,
    render: data => <Tooltip label={data as string}>{data as string}</Tooltip>,
  },
  {
    title: 'Step',
    dataIndex: 'step',
    key: 'step',
    width: 220,
    sorter: true,
    render: (data, { candidate_status }) =>
      [STATUSES.HIRED, STATUSES.FIRED].includes(candidate_status as typeof STATUSES.HIRED | typeof STATUSES.FIRED) ? (
        '—'
      ) : (
        <Tooltip label={data as string}>{data as string}</Tooltip>
      ),
  },
  {
    title: 'Step status',
    dataIndex: 'step_status',
    key: 'step_status',
    width: 220,
    sorter: true,
    render: (status, { candidate_status }) =>
      [STATUSES.HIRED, STATUSES.FIRED].includes(candidate_status as typeof STATUSES.HIRED | typeof STATUSES.FIRED) ? (
        '—'
      ) : (
        <Status type={getJobStatusColor(status as string)}>{status as string}</Status>
      ),
  },
  {
    title: headerCellRender('Assignment date', 'Assignment date', 'assignment_date', sort, setSorting),
    dataIndex: 'assignment_date',
    key: 'assignment_date',
    width: 168,
    sorter: true,
    render: data => <Tooltip label={data as string}>{data as string}</Tooltip>,
  },
  {
    title: 'Interviews',
    dataIndex: 'interviews',
    key: 'interviews',
    width: 168,
    sorter: true,
    render: data => <Tooltip label={data as string}>{data as string}</Tooltip>,
  },
  {
    title: 'Assignment comment',
    dataIndex: 'comment',
    key: 'comment',
    width: 168,
    sorter: true,
    render: data => (
      <Tooltip label={<RichText text={data as string} />}>
        <div className="plain-rich-text">
          <RichText text={data as string} />
        </div>
      </Tooltip>
    ),
  },
  {
    title: headerCellRender('Job offer', 'Job offer', 'has_offer', sort, setSorting),
    dataIndex: 'has_offer',
    key: 'has_offer',
    width: 168,
    sorter: true,
    render: value => {
      const { className, data } = value as AnalyticsCandidateOfferCell;
      return (
        <Tooltip label={data}>
          <span className={className}>{data}</span>
        </Tooltip>
      );
    },
  },
  {
    title: headerCellRender('Offer feedback', 'Offer feedback', 'offer_decision', sort, setSorting),
    dataIndex: 'offer_decision',
    key: 'offer_decision',
    width: 168,
    sorter: true,
    render: value => {
      const { className, data } = value as AnalyticsCandidateOfferCell;
      return (
        <Tooltip label={data}>
          <span className={className}>{data}</span>
        </Tooltip>
      );
    },
  },
  {
    title: 'Result',
    dataIndex: 'result',
    key: 'result',
    width: 264,
    sorter: true,
    render: data => <Tooltip label={data as string}>{data as string}</Tooltip>,
  },
];

export const COLUMNS_BE: Record<string, string> = {
  job: 'Job name',
  candidate_full_name: 'Candidate',
  country: 'Country',
  responsible_recruiter: 'Recruiter',
  step: 'Step',
  step_status: 'Step status',
  assignment_date: 'Assignment date',
  interviews: 'Interviews',
  comment: 'Assignment Comment',
  has_offer: 'Job offer',
  offer_decision: 'Offer feedback',
  result: 'Result',
};

export const OPTIONS: AnalyticsCandidateTableOptions = {
  sorting: { key: 'job', order: 'asc', field: 'Job' },
  limit: { value: 30, label: 30 },
  page: 1,
  offset: 0,
  itemsCount: 0,
};

export const DEFAULT_SHOWN_COLUMNS: Record<string, boolean> = {
  job: true,
  candidate_full_name: true,
  step: true,
  step_status: true,
};
